import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { Avatar } from '@/components/Avatar'
import type { UsersAndContacts } from '@/types/contacts'
import { getAvatarProps } from '@/utils/avatarUtils'

interface ContactItemProps {
  contact: UsersAndContacts
  onPress: (contact: UsersAndContacts) => void
  showGiftMessage?: boolean
  giftMessage?: string
  isDisabled?: boolean
  isSelected?: boolean
  isMultiSelectMode?: boolean
  avatarBg?: string
}

export const ContactItem: React.FC<ContactItemProps> = ({
  contact,
  onPress,
  showGiftMessage = false,
  giftMessage,
  isDisabled = false,
  isSelected = false,
  isMultiSelectMode = false,
  avatarBg = '',
}) => {
  const avatarProps = getAvatarProps({
    first_name: contact.first_name,
    last_name: contact.last_name,
    avatar_path: contact.avatar_path,
    avatarBg: contact.avatarBg,
    id: contact.id,
  })

  const displayName = contact.first_name || contact.username || 'Unknown'
  const subtitle = contact.username
    ? `@${contact.username}`
    : contact.phone_number

  const handlePress = () => {
    if (!isDisabled) {
      onPress(contact)
    }
  }

  return (
    <BaseView
      bgColor="background.cardItemRoot"
      row // ВАЖНО: используем row prop как в RN
      align="center" // ВАЖНО: используем align prop как в RN
      gap={12} // ВАЖНО: используем gap prop как в RN
      className={`
        mb-2 cursor-pointer
        ${isDisabled ? 'opacity-50' : 'hover:opacity-90 active:scale-[0.99]'}
        transition-all duration-150 ease-out
      `}
      style={{
        padding: '12px',
        borderRadius: '34px', // Точно как в RN версии
        minHeight: '72px', // Фиксированная высота как в RN
        ...(isSelected && isMultiSelectMode
          ? {
              // border: '2px solid #6C5CE7',
              backgroundColor: '#232425',
            }
          : {}),
      }}
      onClick={handlePress}
    >
      {/* Avatar */}
      <Avatar
        size={48}
        initials={avatarProps.initials}
        imageUrl={avatarProps.avatarUrl}
        gradientColors={avatarProps.backgroundGradient}
        fullSize={false}
        bgColor={'#353535'}
        // fullPercentage={true}
      />

      {/* Contact Info - точно как в RN CardItem.Content */}
      <BaseView
        className="flex-1 min-w-0"
        style={{
          gap: '6px',
          flexDirection: 'column', // Вертикальная раскладка для текста
          alignItems: 'flex-start',
          width: '100%',
          flex: 1,
        }}
      >
        {/* Name - используем BaseText как в RN */}
        <BaseText
          className="truncate"
          fontSize={16}
          fontFamily="semibold" // Соответствует cardTitle в RN
          color="label.textTextInput"
          style={{
            lineHeight: '20px',
            margin: 0,
            width: '100%',
          }}
        >
          {displayName}
        </BaseText>

        {/* Subtitle - используем BaseText как в RN */}
        {subtitle && (
          <BaseText
            className="truncate"
            fontSize={14}
            fontFamily="regular" // Соответствует cardDescription в RN
            color={contact.username ? 'label.primary' : 'label.description'}
            style={{
              lineHeight: '18px',
              margin: 0,
              width: '100%',
            }}
          >
            {subtitle}
          </BaseText>
        )}
      </BaseView>

      {/* Right side content - точно как в RN CardItem.RightIcon */}
      <BaseView
        style={{
          justifyContent: 'center',
          alignItems: 'flex-end',
          maxWidth: '150px',
          flexShrink: 0,
        }}
      >
        {/* Gift message - точно как в RN NonUserInviteButton */}
        {showGiftMessage && giftMessage && (
          <BaseView
            style={{
              paddingTop: '8px', // responsiveAdapterVertical(8)
              paddingBottom: '8px', // responsiveAdapterVertical(8)
              paddingLeft: '12px', // responsiveAdapter(12)
              paddingRight: '12px', // responsiveAdapter(12)
              backgroundColor: 'var(--color-background-profitFlag)',
              borderRadius: '20px', // Как в RN версии
              border: '1px solid var(--color-border-success)',
            }}
          >
            <BaseText
              fontSize={12}
              fontFamily="medium"
              color="label.flagProfit"
              style={{
                lineHeight: '16px',
                margin: 0,
                textAlign: 'center',
              }}
            >
              {giftMessage}
            </BaseText>
          </BaseView>
        )}

        {/* Multi-select checkbox or Chevron */}
        {!showGiftMessage && isMultiSelectMode && (
          <BaseView
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '12px',
              border: isSelected ? 'none' : '2px solid #B2B3B3',
              backgroundColor: isSelected ? '#6C5CE7' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isSelected && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </BaseView>
        )}

        {/* Chevron - точно как в RN AppIcons chevronRight */}
        {!showGiftMessage && !isMultiSelectMode && (
          <svg
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{
              color: '#B2B3B3',
              flexShrink: 0,
            }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        )}
      </BaseView>
    </BaseView>
  )
}

export default ContactItem
